import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  GridComponent,
  Inject,
  ColumnsDirective,
  ColumnDirective,
  Search,
  Page,
  Sort,
  Filter,
  ExcelExport,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { connect } from "react-redux";
import { Header } from "../../../components";
import { useNavigate } from "react-router-dom";
import axios from "../../../api";
import {
  Button,
} from "@material-ui/core";
import { encrypt } from "../../../utils/crypt";
import Swal from "sweetalert2";
import BackdropComponent from "../../../components/Backdrop";

const Users = (props) => {
  const history = useNavigate();
  const { token, rawPermission, userId } = props;
  const isMounted = useRef(true);

  const permission = useMemo(() => (
    (rawPermission || [])
      .filter((data) => data.modulosAcciones?.id_modulos === 2)
      .map((item) => item.modulosAcciones?.id_acciones)
  ), [rawPermission]);

  const fileName = "usuarios";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const editButton = (row) => {
    const action = () => {
      history(`/users/edit/${encrypt(row.id)}`);
    };
    return (
      <Button
        onClick={action}
        variant="contained"
        size="small"
        style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: '8px' }}
        disabled={!(permission || []).includes(3)}
      >
        Editar
      </Button>
    );
  };

  const deleteButton = (row) => {
    const modalDelete = (id) => {
      if (!permission.includes(4)) {
        console.warn("Sin permisos de eliminación");
        return;
      }
      Swal.fire({
        text: "¿Está seguro que desea eliminar este usuario?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#9d2d39",
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.value) {
          sendDelete(id);
        }
      });
    };

    const sendDelete = async (id) => {
      setLoading(true);
      try {
        const { data } = await axios.delete(`/user/${id}`, {
          data: { id_usuarios: userId },
          headers: { "access-token": token },
        });
        if (data.userId?.success || data.success) {
          Swal.fire({
            text: "Eliminado exitosamente.",
            icon: "success",
            showConfirmButton: false,
            timer: 2000,
          });
          getUsers();
        } else {
          Swal.fire({
            text: data.userId?.message || data.message || "Error al eliminar",
            icon: "error",
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    return (
      <Button
        onClick={() => modalDelete(row.id)}
        variant="contained"
        size="small"
        disabled={!(permission || []).includes(4)}
        style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: '8px' }}
      >
        Eliminar
      </Button>
    );
  };

  useEffect(() => {
    if (Array.isArray(permission) && permission.includes(1) && users.length === 0) {
      getUsers();
    }
  }, [permission, users.length]);

  const getUsers = async () => {
    try {
      const { data } = await axios.post(`/user/getUsers`, {}, {
        headers: { "access-token": token },
      });
      if (isMounted.current) {
        setUsers(data?.users || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const columnsGrid = useMemo(() => [
    { headerText: "Nombres", textAlign: "Center", field: "nombres" },
    { headerText: "Apellidos", textAlign: "Center", field: "apellidos" },
    { headerText: "Documento", textAlign: "Center", field: "documento" },
    { headerText: "Usuario", textAlign: "Center", field: "usuario" },
    { 
      headerText: "Rol", 
      textAlign: "Center", 
      template: (row) => row?.grupoUsuarios?.nombre || "N/A" 
    },
    { headerText: "Editar", textAlign: "Center", width: "110", template: editButton },
    { headerText: "Eliminar", textAlign: "Center", width: "110", template: deleteButton }
  ], [permission]);

  const searchOptions = {
    fields: ["nombres", "apellidos", "documento", "usuario"],
    ignoreCase: true,
    operator: "contains",
    key: "",
  };

  const toolbarOpts = [
    "Search",
    {
      text: "Crear usuario",
      prefixIcon: "e-add",
      id: "add_user",
      align: "Left",
    },
    {
      text: "Exportar",
      prefixIcon: "e-excelexport",
      id: "export_excel",
      align: "Left",
    },
  ];

  const handleToolbarClick = (args) => {
    if (args.item.id === "add_user") {
      history(`/users/create`);
    } else if (args.item.id === "export_excel") {
      dataToExcel(users);
    }
  };

  const dataToExcel = (data) => {
    if (!Array.isArray(data) || data.length === 0) return;
    const arrayExcel = data.map((item) => ({
      Nombre: `${item.nombres || ""} ${item.apellidos || ""}`.trim(),
      Documento: item.documento,
      Usuario: item.usuario,
      Email: item.email,
      Grupo: item.grupoUsuarios?.nombre || "N/A",
    }));
    const ws = XLSX.utils.json_to_sheet(arrayExcel);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }), fileName + ".xlsx");
  };

  if (!Array.isArray(permission) || permission.length === 0) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Ajustes" title="Usuarios" />
        <div className="p-10 text-center">Cargando configuración...</div>
        <BackdropComponent loading={true} />
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      <Header category="Parametrización evaluación docente" title="Usuarios" />
      
      <GridComponent
        dataSource={users}
        width="auto"
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 12 }}
        searchSettings={searchOptions}
        toolbar={toolbarOpts}
        toolbarClick={handleToolbarClick}
        allowTextWrap={true}
        columns={columnsGrid}
      >
        <Inject services={[Search, Page, Sort, Filter, Toolbar, ExcelExport]} />
      </GridComponent>

      <BackdropComponent loading={loading} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.token,
  rawPermission: state.permission || [],
  userId: state.user?.id,
});

export default connect(mapStateToProps, null)(Users);
